"use client";

import type { Variants } from "framer-motion";
import { motion, useMotionValue, useScroll, useSpring } from "framer-motion";
import {
	CalendarCheck2,
	LineChart,
	BarChart as MenuIcon,
	PieChart,
	ShieldCheck,
	TrendingUp,
	Users as UsersIcon,
	XIcon,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type { PropsWithChildren } from "react";
import { useEffect, useId, useRef, useState } from "react";

// Simple animation presets
const fadeUp = {
	hidden: { opacity: 0, y: 24 },
	show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};
const stagger = {
	hidden: {},
	show: { transition: { staggerChildren: 0.12 } },
};

// 3D tilt card
type TiltCardProps = PropsWithChildren<{
	className?: string;
	variants?: Variants;
}>;

function TiltCard({ className = "", variants, children }: TiltCardProps) {
	const ref = useRef<HTMLDivElement | null>(null);
	const rX = useMotionValue(0);
	const rY = useMotionValue(0);
	const spring = { stiffness: 200, damping: 20, mass: 0.6 };
	const rx = useSpring(rX, spring);
	const ry = useSpring(rY, spring);

	const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
		const el = ref.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const px = (e.clientX - rect.left) / rect.width; // 0..1
		const py = (e.clientY - rect.top) / rect.height; // 0..1
		const max = 10; // deg
		rY.set((px - 0.5) * max * 2);
		rX.set(-(py - 0.5) * max * 2);
	};
	const handleLeave = () => {
		rX.set(0);
		rY.set(0);
	};

	return (
		<div style={{ perspective: 1000 }}>
			<motion.div
				ref={ref}
				role="presentation"
				onMouseMove={handleMove}
				onMouseLeave={handleLeave}
				style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
				variants={variants}
				className={"will-change-transform " + className}
				whileHover={{ translateY: -4, scale: 1.01 }}
				transition={{ type: "spring", stiffness: 220, damping: 20 }}
			>
				{children}
			</motion.div>
		</div>
	);
}

export default function Home() {
	const [isScrolled, setIsScrolled] = useState(false);
	const [mobileOpen, setMobileOpen] = useState(false);
	const { scrollY } = useScroll();
	const router = useRouter();
	const featuresId = useId();
	const howId = useId();
	const testimonialsId = useId();
	const contactId = useId();

	useEffect(() => {
		return scrollY.onChange((v) => setIsScrolled(v > 12));
	}, [scrollY]);

	const accent = "#6EFCD9"; // requested accent

	return (
		<div className="min-h-screen bg-black text-white">
			{/* Header */}
			<header
				className={`fixed top-0 inset-x-0 z-50 transition-all ${
					isScrolled
						? "bg-black/70 backdrop-blur-md border-b border-white/10"
						: "bg-transparent"
				}`}
			>
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="h-16 flex items-center justify-between">
						<Link href="/" className="flex items-center gap-3">
							<motion.div
								initial={{ opacity: 0, y: -6, scale: 0.94 }}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								transition={{ duration: 0.6, ease: "easeOut" }}
								whileHover={{ scale: 1.04, rotate: -1 }}
								whileTap={{ scale: 0.98 }}
								className="inline-flex items-center"
							>
								<Image
									src="/logo-1.png"
									alt="BRIK"
									width={204}
									height={58}
									className="h-[2.54rem] w-auto brightness-0 invert"
								/>
							</motion.div>
						</Link>

						<nav className="hidden md:flex items-center gap-8">
							<Link href={`#${featuresId}`} className="hover:text-white/80">
								Fonctionnalités
							</Link>
							<Link href={`#${howId}`} className="hover:text-white/80">
								Processus
							</Link>
							<Link href={`#${testimonialsId}`} className="hover:text-white/80">
								Témoignages
							</Link>
							<Link href={`#${contactId}`} className="hover:text-white/80">
								Contact
							</Link>
							<button
								onClick={() => router.push("/login")}
								className="rounded-full px-5 py-2 text-black font-medium shadow-sm"
								type="button"
								style={{ backgroundColor: accent }}
							>
								Se connecter
							</button>
						</nav>

						<button
							className="md:hidden p-2 rounded-md hover:bg-white/5"
							aria-label="Ouvrir le menu"
							onClick={() => setMobileOpen((s) => !s)}
							type="button"
						>
							{mobileOpen ? (
								<XIcon className="w-6 h-6" />
							) : (
								<MenuIcon className="w-6 h-6" />
							)}
						</button>
					</div>
				</div>

				{/* Mobile menu */}
				{mobileOpen && (
					<div className="md:hidden border-t border-white/10 bg-black/90 backdrop-blur-sm">
						<div className="mx-auto max-w-7xl px-4 py-4 space-y-2">
							<Link
								href={`#${featuresId}`}
								onClick={() => setMobileOpen(false)}
								className="block py-2"
							>
								Fonctionnalités
							</Link>
							<Link
								href={`#${howId}`}
								onClick={() => setMobileOpen(false)}
								className="block py-2"
							>
								Processus
							</Link>
							<Link
								href={`#${testimonialsId}`}
								onClick={() => setMobileOpen(false)}
								className="block py-2"
							>
								Témoignages
							</Link>
							<Link
								href={`#${contactId}`}
								onClick={() => setMobileOpen(false)}
								className="block py-2"
							>
								Contact
							</Link>
							<button
								onClick={() => router.push("/login")}
								className="w-full rounded-full px-5 py-2 text-black font-medium"
								type="button"
								style={{ backgroundColor: accent }}
							>
								Se connecter
							</button>
						</div>
					</div>
				)}
			</header>

			{/* Hero */}
			<section className="relative overflow-hidden pt-28">
				{/* background accents */}
				<div
					aria-hidden
					className="pointer-events-none absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full blur-3xl opacity-25"
					style={{
						background: `radial-gradient(closest-side, ${accent}, transparent)`,
					}}
				/>
				<div
					aria-hidden
					className="pointer-events-none absolute -bottom-24 -right-24 h-[520px] w-[520px] rounded-full blur-3xl opacity-20"
					style={{
						background: `radial-gradient(closest-side, ${accent}, transparent)`,
					}}
				/>

				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<motion.div
						variants={stagger}
						initial="hidden"
						animate="show"
						className="grid lg:grid-cols-2 gap-10 items-center"
					>
						<motion.div variants={fadeUp} className="text-center lg:text-left">
							<div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-white/80 mb-4">
								<span
									className="inline-block h-2 w-2 rounded-full"
									style={{ backgroundColor: accent }}
								/>
								Pilotage de projets immobiliers
							</div>

							<h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
								La tour de contrôle de vos opérations immobilières
							</h1>
							<p className="mt-5 text-lg text-white/70 max-w-xl mx-auto lg:mx-0">
								Centralisez données, budgets et échéanciers. Collaborez en temps
								réel. Décidez avec confiance grâce à des indicateurs clairs.
							</p>

							<div className="mt-8 flex flex-col sm:flex-row items-center gap-4 sm:gap-3 justify-center lg:justify-start">
								<Link href="/login">
									<button
										className="rounded-full px-6 py-3 text-black font-medium shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
										type="button"
										style={{ backgroundColor: accent }}
									>
										Commencer
									</button>
								</Link>
								<Link
									href="https://calendly.com/koutar-mohamed/30min"
									target="_blank"
								>
									<button
										className="rounded-full px-6 py-3 border border-white/20 hover:border-white/40 transition"
										type="button"
									>
										Planifier une démo
									</button>
								</Link>
							</div>

							<div className="mt-8 grid grid-cols-3 gap-6 text-center lg:text-left">
								<div>
									<div
										className="text-3xl font-semibold"
										style={{ color: accent }}
									>
										+35%
									</div>
									<div className="text-xs text-white/60">
										Gain de productivité
									</div>
								</div>
								<div>
									<div
										className="text-3xl font-semibold"
										style={{ color: accent }}
									>
										-20%
									</div>
									<div className="text-xs text-white/60">Délais réduits</div>
								</div>
								<div>
									<div
										className="text-3xl font-semibold"
										style={{ color: accent }}
									>
										100%
									</div>
									<div className="text-xs text-white/60">Vue 360° projets</div>
								</div>
							</div>
						</motion.div>

						<motion.div variants={fadeUp} className="relative">
							<div
								className="absolute -inset-4 rounded-2xl opacity-40 blur-2xl"
								style={{
									background: `linear-gradient(135deg, ${accent}33, transparent 60%)`,
								}}
							/>
							<div className="relative rounded-2xl ring-1 ring-white/10 bg-white/5 p-2">
								<Image
									src="/all.png"
									alt="Aperçu BRIK"
									width={1200}
									height={800}
									className="rounded-xl w-full h-auto"
									priority
								/>
							</div>
						</motion.div>
					</motion.div>
				</div>
			</section>

			{/* Logos */}
			{/* <section className="py-10">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="flex flex-wrap items-center justify-center gap-10 opacity-70">
						<Image
							src="/logo-1.png"
							width={110}
							height={32}
							alt="BRIK"
							className="h-7 w-auto brightness-0 invert"
						/>
						<div className="h-6 w-px bg-white/10" />
						<span className="text-sm text-white/60">
							Conçu pour les promoteurs et leurs équipes
						</span>
					</div>
				</div>
			</section> */}

			{/* Features */}
			<section id={featuresId} className="py-20">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="text-center max-w-3xl mx-auto">
						<h2 className="text-3xl sm:text-4xl font-semibold">
							Tout ce qu’il vous faut pour piloter
						</h2>
						<p className="mt-3 text-white/70">
							Des modules puissants, pensés pour le terrain et validés par la
							direction.
						</p>
					</div>

					<motion.div
						variants={stagger}
						initial="hidden"
						whileInView="show"
						viewport={{ once: true, margin: "-80px" }}
						className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
					>
						{[
							{
								key: "suivi-financier",
								icon: <TrendingUp className="w-5 h-5" />,
								title: "Suivi financier",
								desc: "Capex, cashflow et ROI en un clin d’œil.",
							},
							{
								key: "echeanciers",
								icon: <CalendarCheck2 className="w-5 h-5" />,
								title: "Échéanciers",
								desc: " jalons, retards et alertes intelligentes.",
							},
							{
								key: "collaboration",
								icon: <UsersIcon className="w-5 h-5" />,
								title: "Collaboration",
								desc: "Tous les acteurs alignés sur une source unique.",
							},
							{
								key: "conformite",
								icon: <ShieldCheck className="w-5 h-5" />,
								title: "Conformité",
								desc: "Respect des contraintes et traçabilité.",
							},
							{
								key: "reporting",
								icon: <PieChart className="w-5 h-5" />,
								title: "Reporting",
								desc: "Rapports prêts à partager avec les parties prenantes.",
							},
							{
								key: "analytique",
								icon: <LineChart className="w-5 h-5" />,
								title: "Analytique",
								desc: "KPIs en temps réel pour décider vite.",
							},
						].map((f) => (
							<TiltCard
								key={f.key}
								variants={fadeUp}
								className="group relative rounded-2xl border border-white/10 bg-white/[0.04] p-6 shadow-sm hover:bg-white/[0.06] transition"
							>
								<div
									className="absolute inset-x-0 -top-px h-px"
									style={{
										background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
									}}
								/>
								<div className="flex items-center gap-3">
									<div
										className="rounded-full p-2 ring-1 ring-white/10"
										style={{ backgroundColor: "white" }}
									>
										<div className="text-black" style={{ color: "#0b0b0b" }}>
											{f.icon}
										</div>
									</div>
									<h3 className="font-medium text-lg">{f.title}</h3>
								</div>
								<p className="mt-3 text-sm text-white/70">{f.desc}</p>
							</TiltCard>
						))}
					</motion.div>
				</div>
			</section>

			{/* Process */}
			<section id={howId} className="py-20 bg-white text-black">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="text-center max-w-3xl mx-auto">
						<h2 className="text-3xl sm:text-4xl font-semibold">
							De l’idée à la livraison, sans friction
						</h2>
						<p className="mt-3 text-gray-600">
							Un processus clair qui garde tout le monde aligné.
						</p>
					</div>

					<div className="mt-12 grid md:grid-cols-3 gap-6">
						{["Planifier", "Exécuter", "Rapporter"].map((t, i) => (
							<div
								key={t}
								className="relative rounded-2xl border border-black/[0.08] bg-white p-6 shadow-sm"
							>
								<div
									className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-black"
									style={{ backgroundColor: accent }}
								>
									{i + 1}
								</div>
								<h3 className="mt-4 text-xl font-semibold">{t}</h3>
								<p className="mt-2 text-sm text-gray-600">
									{i === 0 &&
										"Analyse de faisabilité, budgets, foncier et hypothèses."}
									{i === 1 &&
										"Suivi des tâches, jalons, risques et dépenses en temps réel."}
									{i === 2 &&
										"Rapports et KPIs pour piloter, partager et optimiser."}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Testimonials */}
			<section id={testimonialsId} className="py-20">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="text-center max-w-3xl mx-auto">
						<h2 className="text-3xl sm:text-4xl font-semibold">
							Ils pilotent sur BRIK
						</h2>
						<p className="mt-3 text-white/70">
							Ce que disent des dirigeants et chefs de projets.
						</p>
					</div>

					<motion.div
						variants={stagger}
						initial="hidden"
						whileInView="show"
						viewport={{ once: true, margin: "-80px" }}
						className="mt-12 grid md:grid-cols-2 gap-6"
					>
						{[
							{
								name: "Mohamed ROUTAK",
								role: "Directeur, Promo'Demain",
								quote:
									"Les indicateurs financiers et le suivi des jalons nous ont fait gagner un temps précieux.",
							},
							{
								name: "Rabie RAFIK",
								role: "Directeur, Horizon Properties",
								quote:
									"La coordination entre équipes s’est fluidifiée et nos délais ont baissé de 20%.",
							},
						].map((t) => (
							<motion.blockquote
								key={t.name}
								variants={fadeUp}
								className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
							>
								<div className="flex items-center gap-3">
									<div
										className="h-10 w-10 rounded-full"
										style={{ backgroundColor: `${accent}66` }}
									/>
									<div>
										<div className="font-medium">{t.name}</div>
										<div className="text-sm text-white/60">{t.role}</div>
									</div>
								</div>
								<p className="mt-4 text-white/90 italic">“{t.quote}”</p>
							</motion.blockquote>
						))}
					</motion.div>
				</div>
			</section>

			{/* Final CTA */}
			<section id={contactId} className="py-20">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-8 sm:p-12 text-center">
						<div
							className="absolute -inset-1 rounded-3xl opacity-20 blur-2xl"
							style={{
								background: `radial-gradient(closest-side, ${accent}, transparent)`,
							}}
						/>
						<h2 className="relative text-3xl sm:text-4xl font-semibold">
							Passez au pilotage moderne
						</h2>
						<p className="relative mt-3 text-white/75 max-w-2xl mx-auto">
							Essayez BRIK dès aujourd’hui et prenez de meilleures décisions,
							plus vite.
						</p>
						<div className="relative mt-8 flex flex-col sm:flex-row gap-4 justify-center">
							<Link href="/login">
								<button
									className="rounded-full px-6 py-3 text-black font-medium"
									style={{ backgroundColor: accent }}
									type="button"
								>
									Se connecter
								</button>
							</Link>
							<Link
								href="https://calendly.com/koutar-mohamed/30min"
								target="_blank"
							>
								<button
									className="rounded-full px-6 py-3 border border-white/20 hover:border-white/40 transition"
									type="button"
								>
									Planifier une démo
								</button>
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* Footer */}
			<footer className="border-t border-white/10 py-10">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col md:flex-row items-center justify-between gap-6">
						<div className="flex items-center gap-3">
							<Image
								src="/logo-1.png"
								alt="BRIK"
								width={120}
								height={32}
								className="h-6 w-auto brightness-0 invert"
							/>
							<span className="text-white/50 text-sm">
								© {new Date().getFullYear()} BRIK
							</span>
						</div>
						<nav className="flex items-center gap-6 text-sm text-white/70">
							<Link href="#features" className="hover:text-white">
								Fonctionnalités
							</Link>
							<Link href="#how" className="hover:text-white">
								Processus
							</Link>
							<Link href="#contact" className="hover:text-white">
								Contact
							</Link>
							<Link href="/privacy" className="hover:text-white">
								Confidentialité
							</Link>
							<Link href="/terms" className="hover:text-white">
								CGU
							</Link>
						</nav>
					</div>
				</div>
			</footer>
		</div>
	);
}
